"use client";

import { useWallet } from "@solana/wallet-adapter-react";
import { motion } from "framer-motion";
import { t } from "i18next";
import Image from "next/image";
import type { FC } from "react";
import { IoChevronDown } from "react-icons/io5";

import { Button } from "../atoms/Button";
import { useSettingsPopup } from "contexts/SettingsPopupContext";
import { useLocalStorageUserSettings } from "hooks/useLocalStorageUserSettings";
import { useWalletConnector } from "hooks/useWalletConnector";
import Images from "styles/image-library";
import { getWalletIcon } from "utils/get-wallet-icon";
import { shortenAddress } from "utils/shorten-address";

import { WalletConnectMode } from "./SettingsPopupContent/WalletConnectBody";

interface WalletButtonProps {
  variant: "normal" | "header" | "smart" | "no-wallet" | "stake";
  className?: string;
}

export const WalletButton: FC<WalletButtonProps> = ({ variant, className }) => {
  const walletConnector = useWalletConnector();
  const { wallet, wallets } = useWallet();

  const { openSettingsPopup, closeSettingsPopup } = useSettingsPopup();

  const [savedRecentWallet, termsAgreement, jurisdictionConsent] =
    useLocalStorageUserSettings();

  const walletIcon = (name: string | null | undefined) =>
    getWalletIcon(wallets.find((w) => w.adapter.name === name)?.adapter.name);

  const isSmart =
    variant === "smart" &&
    savedRecentWallet &&
    termsAgreement &&
    jurisdictionConsent;
  const isNoWallet = variant === "no-wallet";
  const isStake = variant === "stake";
  const connectText = t("walletConnect.button.connect");

  if (!wallet?.adapter.connected) {
    if (isSmart) {
      return (
        <motion.button
          whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
          type="button"
          className={`flex relative items-center justify-center rounded-full px-2 py-[7px] h-[56px] max-w-none md:max-w-[327px] focus:outline-none bg-primary text-white active:bg-button-active hover:bg-button-hover disabled:bg-opacity-50 ${className}`}
          onClick={() => walletConnector.connectWallet(savedRecentWallet)}
        >
          <Image
            className="flex absolute left-[28px] w-6 h-6"
            src={walletIcon(savedRecentWallet)}
            alt="wallet icon"
            width={24}
            height={24}
          />
          <div>
            <p className="text-lg md:text-xl font-semibold eading-[24px] block">
              {connectText}
            </p>
            {savedRecentWallet && (
              <p className="text-xs sm:text-sm leading-[18px] md:leading-[20px] opacity-70 block">
                {t("walletConnect.button.last-used").replace(
                  "{{wallet}}",
                  savedRecentWallet
                )}
              </p>
            )}
          </div>
          {savedRecentWallet && (
            <motion.div
              whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
              className="flex absolute right-2 h-10 w-12 rounded-[56px] items-center justify-center bg-button-hover hover:bg-primary"
              onClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                walletConnector.connectWallet(null);
              }}
            >
              <IoChevronDown className="w-4 h-4" />
            </motion.div>
          )}
        </motion.button>
      );
    }
    if (isNoWallet) {
      return (
        <motion.button
          whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
          type="button"
          className="mb-0.5 text-primary text-base font-semibold content-center"
          onClick={() => {
            walletConnector.openWalletDrawer(WalletConnectMode.NoWallet);
          }}
        >
          {t("walletConnect.no-wallet.prompt")}
        </motion.button>
      );
    }
    if (isStake) {
      return (
        <Button
          label={t("walletConnect.button.stake") ?? ""}
          variant="primary"
          onClick={() => walletConnector.connectWallet(null)}
          className={`max-w-none md:max-w-[327px] ${className}`}
          size="large"
          leftIcon={Images.icons_stake_arrow}
          leftIconSizePx={24}
        />
      );
    }

    if (variant === "header") {
      return (
        <motion.button
          whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
          className={`border-2 min-h-[32px] rounded-full border-primary border-opacity-25 px-2 flex gap-1.5 justify-between items-center font-medium text-sm active:border-button-active active:text-button-active hover:border-button-hover hover:text-button-hover ${className}`}
          onClick={() => walletConnector.connectWallet(null)}
        >
          {connectText}
        </motion.button>
      );
    }

    return (
      <Button
        label={connectText}
        variant="primary"
        onClick={() => walletConnector.connectWallet(null)}
        className={`max-w-none md:max-w-[327px] px-2 ${className}`}
        size="large"
        leftIcon={Images.icons_wallet}
        leftIconSizePx={24}
      />
    );
  }

  return (
    <motion.button
      whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
      className={`border-2 min-h-[32px] rounded-full border-primary border-opacity-25 px-2 flex gap-1.5 justify-between items-center font-medium text-sm active:border-button-active active:text-button-active hover:border-button-hover hover:text-button-hover ${className}`}
      onClick={() =>
        openSettingsPopup(
          {
            title: "Wallet",
          },
          { closeSettingsPopup, type: "wallet" },
          true
        )
      }
    >
      <Image
        src={wallet.adapter.icon}
        alt="Wallet icon"
        height={16}
        width={16}
      />
      {shortenAddress(wallet.adapter.publicKey?.toString() ?? "")}
    </motion.button>
  );
};
